import { useQuery, useMutation } from '@tanstack/react-query';
import axiosInstance from '../../lib/axios';
import type {
  ModuleInfoDto,
  ValidateEnableModuleResponse,
  ValidateDisableModuleResponse,
  DependencyGraphResponse,
} from '../../types/subscriptions';

const QUERY_KEYS = {
  dependencies: (moduleKey: string) => ['module-dependencies', moduleKey] as const,
  dependents: (moduleKey: string) => ['module-dependents', moduleKey] as const,
  dependencyGraph: (orgId: string) => ['dependency-graph', orgId] as const,
};

/**
 * Hook to fetch dependencies for a specific module
 * Returns the list of modules that the specified module depends on
 */
export const useModuleDependencies = (moduleKey: string, enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.dependencies(moduleKey),
    queryFn: async () => {
      const { data } = await axiosInstance.get<ModuleInfoDto[]>(
        `/features/modules/${moduleKey}/dependencies`,
      );
      return data;
    },
    enabled: enabled && !!moduleKey,
  });
};

/**
 * Hook to fetch dependent modules for a specific module
 * Returns the list of modules that depend on the specified module
 */
export const useModuleDependents = (moduleKey: string, enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.dependents(moduleKey),
    queryFn: async () => {
      const { data } = await axiosInstance.get<ModuleInfoDto[]>(
        `/features/modules/${moduleKey}/dependents`,
      );
      return data;
    },
    enabled: enabled && !!moduleKey,
  });
};

/**
 * Hook to validate enabling a module
 * Pre-validates module enablement and returns:
 * - Whether validation passed
 * - List of missing dependencies
 * - List of modules that will be auto-enabled
 */
export const useValidateEnableModule = () => {
  return useMutation({
    mutationFn: async (moduleKey: string) => {
      const { data } = await axiosInstance.post<ValidateEnableModuleResponse>(
        `/features/modules/${moduleKey}/validate-enable`,
      );
      return data;
    },
  });
};

/**
 * Hook to validate disabling a module
 * Pre-validates module disablement and returns:
 * - Whether validation passed
 * - List of affected modules
 * - List of modules that will be cascade-disabled
 */
export const useValidateDisableModule = () => {
  return useMutation({
    mutationFn: async (moduleKey: string) => {
      const { data } = await axiosInstance.post<ValidateDisableModuleResponse>(
        `/features/modules/${moduleKey}/validate-disable`,
      );
      return data;
    },
  });
};

/**
 * Hook to fetch the dependency graph for an organization
 * Returns nodes and edges for visualization
 * Optionally filtered by organization ID
 */
export const useDependencyGraph = (orgId?: string, enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.dependencyGraph(orgId || 'all'),
    queryFn: async () => {
      const url = orgId
        ? `/features/dependency-graph?organizationId=${orgId}`
        : '/features/dependency-graph';
      const { data } = await axiosInstance.get<DependencyGraphResponse>(url);
      return data;
    },
    enabled,
  });
};
